/**
 * The admin rail's transport.
 *
 * Everything here goes through `staffClient` from @bymyword/api-types, which reaches
 * `/api/admin/*` and NOTHING else. That partition is not decoration: a staff token and a
 * candidate token are indistinguishable on the wire (both HS256 bearers; only an audience
 * `:staff` + a `purpose` claim INSIDE the token separate them, invisible to any client), so the
 * path domain is the one place the two rails can be kept apart on this side. Before that seam
 * existed, `candidateClient.get('/api/admin/...')` compiled cleanly.
 *
 * Two clients, because the rail has two auth states:
 *   - `authApi`  — provider returns null. The `/api/admin/auth/*` sign-in routes take no token,
 *     and this client is also what rotation uses, so refreshing can never recurse into itself.
 *   - `adminApi` — provider returns the live staff token, rotating it when it is about to
 *     expire. Called per request, so a rotation is picked up without rebuilding the client.
 */
import { ApiError, staffClient, type AccessTokenProvider } from '@bymyword/api-types';
import { useStaffSession, signOut } from '@/auth/session';
import type {
  AdminRequest,
  AdminUsageDashboard,
  AdminUser,
  DeadLetter,
  InviteBurstFlag,
  LinkedAccounts,
  NeedsReviewItem,
  ReferralFraudFlag,
  RefundAbuseFlag,
  StaffTokens,
  Suppression,
  VelocityFlag,
} from './wire';

// Same-origin (blank VITE_API_BASE_URL, the documented prod default) must still resolve to an
// ABSOLUTE URL: the generated client builds requests with `new URL(...)`, and `new URL('/api/x')`
// with no base throws. Fall back to the page origin, exactly as referee-web does.
const BASE_URL =
  (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '') ||
  (typeof window !== 'undefined' ? window.location.origin : '');

/** Rows per page. The API defaults to 25 and caps at 100; the queue is specified as 25. */
export const PAGE_SIZE = 25;

/** The token is rotated this long before it expires, so a request never races the clock. */
const ROTATE_BEFORE_EXPIRY_MS = 60_000;

/**
 * The unauthenticated leg. Its provider returns null unconditionally — not "usually null": if
 * this client ever attached a token, `refresh` would call the provider, which would call
 * `refresh`, forever.
 */
const authApi = staffClient(BASE_URL, () => null);

let inFlightRotation: Promise<string | null> | null = null;

function accessTokenIsFresh(expiresAt: string | null): boolean {
  if (expiresAt === null) return false;
  const expiry = new Date(expiresAt).getTime();
  if (Number.isNaN(expiry)) return false;
  return expiry - Date.now() > ROTATE_BEFORE_EXPIRY_MS;
}

/**
 * Rotate the pair. Deduped: several queries firing at once must produce ONE refresh, or they
 * race to spend the same single-use refresh token and the loser's replay revokes the family
 * (the rail revokes a whole family on a replayed rotation — that is the server behaving
 * correctly, and it would sign the reviewer out mid-action).
 */
function rotate(refreshToken: string): Promise<string | null> {
  inFlightRotation ??= authApi
    .post('/api/admin/auth/refresh', { body: { refreshToken } })
    .then((tokens) => {
      useStaffSession.getState().rotate(tokens);
      return tokens.accessToken;
    })
    .catch(() => {
      // A refresh that fails is a session that is over. Drop it rather than letting a stale
      // token drive a screenful of 401s.
      signOut();
      return null;
    })
    .finally(() => {
      inFlightRotation = null;
    });

  return inFlightRotation;
}

/**
 * The live staff token, rotated when it is nearly expired. Returning null (signed out) makes
 * the client omit the header entirely — the request still goes, and the rail answers 401, which
 * is the correct outcome for a call that should not have been made.
 */
const tokenProvider: AccessTokenProvider = async () => {
  const { accessToken, accessTokenExpiresAt, refreshToken } = useStaffSession.getState();
  if (accessToken !== null && accessTokenIsFresh(accessTokenExpiresAt)) return accessToken;
  if (refreshToken !== null) return rotate(refreshToken);
  return accessToken;
};

const adminApi = staffClient(BASE_URL, tokenProvider);

// --- staff auth ---------------------------------------------------------------

/**
 * Ask for a sign-in code.
 *
 * The response is NEUTRAL and that is the whole point: `StaffOtpRequestResponse` is one shape
 * with one constant value, byte-identical for a staff address and a stranger's. Callers get
 * `void` here on purpose — there is deliberately no field that varies with existence, and
 * handing the body to the UI would only invite someone to render it as if it meant something.
 */
export async function requestStaffOtp(email: string, captchaToken: string): Promise<void> {
  await authApi.post('/api/admin/auth/otp/request', { body: { email, captchaToken } });
}

export function verifyStaffOtp(email: string, code: string): Promise<StaffTokens> {
  return authApi.post('/api/admin/auth/otp/verify', { body: { email, code } });
}

// --- usage dashboard ---------------------------------------------------------

export function fetchUsageDashboard(): Promise<AdminUsageDashboard> {
  return adminApi.get('/api/admin/dashboard');
}

// --- needs-review queue -------------------------------------------------------

export interface Page<T> {
  items: T[];
  total: number;
}

/** The queue, oldest-first (the API orders it; the console does not re-sort). */
export function fetchReviewQueue(offset: number): Promise<Page<NeedsReviewItem>> {
  return adminApi.get('/api/admin/review/queue', {
    query: { limit: PAGE_SIZE, offset },
  });
}

export function approveReview(id: string, note?: string): Promise<{ status: string }> {
  return adminApi.post('/api/admin/review/{id}/approve', {
    pathParams: { id },
    body: { note: note?.trim() || null },
  });
}

export function rejectReview(id: string, note?: string): Promise<{ status: string }> {
  return adminApi.post('/api/admin/review/{id}/reject', {
    pathParams: { id },
    body: { note: note?.trim() || null },
  });
}

// --- break glass --------------------------------------------------------------

/**
 * The ONE reason code the wire accepts. A closed enum, not free text, and the constraint is
 * load-bearing: the reason lands in `audit_log.metadata`, the schema's one PII-free table — the
 * reason it can be retained after an erasure request scrubs everything else. A typed-in
 * justification would eventually contain a name, or a quote from the very transcript being
 * revealed, in the one place that survives deletion. So: no free-text box, anywhere.
 */
export const BREAK_GLASS_REASON = 'moderation_review';

/**
 * What a break-glass attempt produced. A union rather than a nullable string because the three
 * outcomes are genuinely different answers and the UI must not collapse them:
 *   - `revealed`      — the words are here.
 *   - `no_transcript` — allowed, but the request diverted before it was ever transcribed.
 *                       "Nothing to see", NOT "you may not see it" (the API says 200 + null for
 *                       exactly this reason). Rendering it as a denial sends a reviewer hunting
 *                       a permission problem that does not exist.
 *   - `denied`        — refused. A bare 403, identical for "not in needs_review" and "no such
 *                       request", so probing ids reveals nothing. The reason stays server-side
 *                       on the audit row; we cannot show one and must not invent one.
 */
export type BreakGlassOutcome =
  { kind: 'revealed'; transcript: string } | { kind: 'no_transcript' } | { kind: 'denied' };

/**
 * Reveal a transcript under audited break-glass.
 *
 * A POST, and never wrapped in anything that fires on its own. Both the reveal AND the denial
 * are audited server-side, in the same transaction as the read and committed BEFORE any text is
 * returned — so calling this function is, permanently, a record of a human choosing to read a
 * referee's private words. Every caller must be a deliberate act by that human.
 *
 * NOTE: the OpenAPI document now declares this endpoint's full response set (200/400/401/403),
 * so the generated types and the runtime agree. The catch below is still required — a declared
 * 403 is only a type: the client throws every non-2xx as an `ApiError`, and turning that denial
 * into the calm `denied` outcome (rather than an unhandled error) is this function's job.
 */
export async function breakGlassTranscript(id: string): Promise<BreakGlassOutcome> {
  try {
    const result = await adminApi.post('/api/admin/references/{id}/transcript/break-glass', {
      pathParams: { id },
      body: { reason: BREAK_GLASS_REASON },
    });
    return result.transcript === null
      ? { kind: 'no_transcript' }
      : { kind: 'revealed', transcript: result.transcript };
  } catch (error) {
    if (error instanceof ApiError && error.status === 403) return { kind: 'denied' };
    throw error;
  }
}

// --- dead letters -------------------------------------------------------------

export function fetchDeadLetters(offset: number): Promise<Page<DeadLetter>> {
  return adminApi.get('/api/admin/notifications/dead-letters', {
    query: { limit: PAGE_SIZE, offset },
  });
}

export function retryDeadLetter(id: string): Promise<{ status: string }> {
  return adminApi.post('/api/admin/notifications/dead-letters/{id}/retry', {
    pathParams: { id },
  });
}

// --- suppressions -------------------------------------------------------------

/**
 * The only two reasons staff may write.
 *
 * `bounce`, `complaint`, `opt_out` and `stop` are 400 BY DESIGN, and not offered here at all.
 * They are provenance claims — "a provider webhook told us" / "the person themselves asked" —
 * that staff have no standing to make. Offering them in a dropdown would invite recording an
 * admin's decision as the recipient's own words. The 400 is the rail holding a line, not a bug.
 */
export const STAFF_SUPPRESSION_REASONS = [
  { value: 'admin', label: 'Admin', detail: 'We decided to stop contacting this address.' },
  {
    value: 'abuse_report',
    label: 'Abuse report',
    detail: 'This address was reported for abuse.',
  },
] as const;

export type StaffSuppressionReason = (typeof STAFF_SUPPRESSION_REASONS)[number]['value'];

export type SuppressionChannel = 'email' | 'phone';

export function fetchSuppressions(
  offset: number,
  channel?: SuppressionChannel,
): Promise<Page<Suppression>> {
  return adminApi.get('/api/admin/suppressions', {
    query: { limit: PAGE_SIZE, offset, channel },
  });
}

export function addSuppression(input: {
  channel: SuppressionChannel;
  address: string;
  reason: StaffSuppressionReason;
  sourceDetail?: string;
}): Promise<{ status: string }> {
  return adminApi.post('/api/admin/suppressions', {
    body: {
      channel: input.channel,
      address: input.address,
      reason: input.reason,
      sourceDetail: input.sourceDetail?.trim() || null,
    },
  });
}

export function checkSuppression(
  channel: SuppressionChannel,
  address: string,
): Promise<{ suppressed: boolean }> {
  return adminApi.post('/api/admin/suppressions/check', { body: { channel, address } });
}

export function removeSuppression(
  channel: SuppressionChannel,
  address: string,
): Promise<{ status: string }> {
  return adminApi.post('/api/admin/suppressions/remove', { body: { channel, address } });
}

// --- lookup -------------------------------------------------------------------

export function fetchRequest(id: string): Promise<AdminRequest> {
  return adminApi.get('/api/admin/requests/{id}', { pathParams: { id } });
}

export function fetchUser(id: string): Promise<AdminUser> {
  return adminApi.get('/api/admin/users/{id}', { pathParams: { id } });
}

export function findUserByEmail(email: string): Promise<AdminUser> {
  return adminApi.get('/api/admin/users', { query: { email } });
}

// --- fraud flags --------------------------------------------------------------

/**
 * Four read-only detective queues, one per correlation signal. None of them offers an enforcement
 * action, and the copy must never imply one: every row is a signal a human weighs, not a verdict.
 * A shared IP is a household, an office or a carrier NAT far more often than it is a farm; high
 * request volume is usually legitimate. The API declines to reverse a refund, revoke a grant or
 * suspend an account from here for exactly that reason — so there is no button to add.
 */

export function fetchFraudFlags(offset: number): Promise<Page<ReferralFraudFlag>> {
  return adminApi.get('/api/admin/fraud/referrals', {
    query: { limit: PAGE_SIZE, offset },
  });
}

/** Sign-in IPs used by several distinct accounts — the cross-account velocity signal. */
export function fetchVelocityFlags(offset: number): Promise<Page<VelocityFlag>> {
  return adminApi.get('/api/admin/fraud/velocity', {
    query: { limit: PAGE_SIZE, offset },
  });
}

/** Candidates creating reference requests at unusual volume inside the review window. */
export function fetchInviteBurstFlags(offset: number): Promise<Page<InviteBurstFlag>> {
  return adminApi.get('/api/admin/fraud/invite-bursts', {
    query: { limit: PAGE_SIZE, offset },
  });
}

/** Users at or over the refunded-purchase threshold across the Stripe and IAP rails together. */
export function fetchRefundFlags(offset: number): Promise<Page<RefundAbuseFlag>> {
  return adminApi.get('/api/admin/fraud/refunds', {
    query: { limit: PAGE_SIZE, offset },
  });
}

// --- linked accounts (device/IP correlation for one account) ------------------

/**
 * The accounts that share a sign-in IP or a registered device with one account, strongest signal
 * first — the natural next click from a velocity, burst or refund row, which each carry a user id.
 *
 * NOT paged: the response is `{ userId, items }` with no total. The server orders and bounds it;
 * the console renders what it is given. This read WRITES an audit_log row — it is a targeted read
 * of a named person's correlation graph — so every caller must be a deliberate act, never fired on
 * render or hover. An unknown id and an id with no correlations both return an empty list (the same
 * answer), so probing ids reveals nothing.
 */
export function fetchLinkedAccounts(userId: string): Promise<LinkedAccounts> {
  return adminApi.get('/api/admin/users/{id}/linked-accounts', {
    pathParams: { id: userId },
  });
}

/** The rail's coarse error code (`{ error: "..." }`), when the body carried one. */
export function errorCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const problem = error.problem as { error?: unknown } | undefined;
  return typeof problem?.error === 'string' ? problem.error : null;
}

export { ApiError };
